import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

import { createServices } from 'api/Service';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import {
  SERVICE_CREATED_ERROR_MESSAGE,
  SERVICE_CREATED_SUCCESS_MESSAGE,
  SERVICE_QUERY_KEY
} from 'components/shared/NewServiceModal/lib/constants';

import { buildServicesPayload } from '../lib/helpers';

import { useServicesCatalog } from './useServicesCatalog';

const MIN_ITEMS_FOR_CONTROLS = 10;
const DEFAULT_LIMIT = 50;
const DEFAULT_MULTIPLIER = 100;

const getInitialSpecialty = (practice) =>
  practice?.profession_group_id ? String(practice.profession_group_id) : 'all';

export const useAddServicesModal = ({ practice, practiceId, onServicesAdded, onClose, isOpen }) => {
  const navigate = useNavigate();
  const numericPracticeId = Number(practiceId);

  const [selectedRows, setSelectedRows] = useState([]);
  const [standardChargeMultiplier, setStandardChargeMultiplier] = useState(DEFAULT_MULTIPLIER);
  const [allowableMultiplier, setAllowableMultiplier] = useState(DEFAULT_MULTIPLIER);
  const [selfPayMultiplier, setSelfPayMultiplier] = useState(DEFAULT_MULTIPLIER);
  const [selectedSpecialty, setSelectedSpecialty] = useState(() => getInitialSpecialty(practice));
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [filters, setFilters] = useState({});
  const [localEdits, setLocalEdits] = useState({});
  const [removedCodes, setRemovedCodes] = useState([]);
  const [shouldShowControls, setShouldShowControls] = useState(false);

  const {
    tabsData,
    filteredData,
    paginatedAndFilteredData,
    totalItems,
    isFetching,
    isError,
    refetchCptCodes,
    retry
  } = useServicesCatalog({
    numericPracticeId,
    practiceProfessionGroupId: practice?.profession_group_id,
    kind: practice?.kind,
    page,
    limit,
    filters,
    selectedSpecialty,
    standardChargeMultiplier,
    allowableMultiplier,
    selfPayMultiplier,
    localEdits,
    removedCodes,
    enabled: isOpen
  });

  const hasFiltersApplied = Object.keys(filters || {}).length > 0;
  const showControls = shouldShowControls || totalItems >= MIN_ITEMS_FOR_CONTROLS || page > 1;

  const { mutateAsync: createServicesHandler, isLoading } = useCustomMutation({
    mutationFn: (params) => createServices(navigate, params),
    invalidateQueryKey: SERVICE_QUERY_KEY,
    successMessage: SERVICE_CREATED_SUCCESS_MESSAGE,
    errorMessage: SERVICE_CREATED_ERROR_MESSAGE
  });

  const onSelectionChanged = useCallback((gridRef) => {
    setSelectedRows(gridRef?.api.getSelectedRows() ?? []);
  }, []);

  useEffect(() => {
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows.length === 0) return prevSelectedRows;
      const selectedCodes = new Set(prevSelectedRows.map((row) => row.code));
      return filteredData.filter((row) => selectedCodes.has(row.code));
    });
  }, [filteredData]);

  const handleStandardChargeChange = (e) => setStandardChargeMultiplier(e.target.value);
  const handleAllowableChange = (e) => setAllowableMultiplier(e.target.value);
  const handleSelfPayChange = (e) => setSelfPayMultiplier(e.target.value);

  const handleAddServices = async () => {
    if (selectedRows.length === 0) return;
    const services = buildServicesPayload(selectedRows, localEdits);
    const data = await createServicesHandler({ services, practiceId: numericPracticeId });
    if (data?.code === 0) {
      onServicesAdded?.();
      onClose();
    }
  };

  const handleCreateNewService = async (newService) => {
    const data = await createServicesHandler({
      services: [newService],
      practiceId: numericPracticeId
    });
    if (data?.code !== 0) return false;
    refetchCptCodes();
    return true;
  };

  const removeCode = useCallback((code) => {
    setRemovedCodes((prev) => (prev.includes(code) ? prev : [...prev, code]));
    setLocalEdits((prev) => {
      const { [code]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleSpecialtyChange = useCallback((specialty) => {
    setSelectedSpecialty(specialty);
    setPage(1);
  }, []);

  const handleLimitChange = useCallback((newLimit) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  const changeFilters = useCallback((updater) => {
    setFilters((prevFilters) => {
      const currentFilters = prevFilters || {};
      const newFilters = typeof updater === 'function' ? updater(currentFilters) : updater;
      return newFilters || {};
    });
    setPage(1);
  }, []);

  useEffect(() => {
    if (!hasFiltersApplied && totalItems >= MIN_ITEMS_FOR_CONTROLS) {
      setShouldShowControls(true);
    }
  }, [totalItems, hasFiltersApplied]);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedRows([]);
    setStandardChargeMultiplier(DEFAULT_MULTIPLIER);
    setAllowableMultiplier(DEFAULT_MULTIPLIER);
    setSelfPayMultiplier(DEFAULT_MULTIPLIER);
    setSelectedSpecialty(getInitialSpecialty(practice));
    setPage(1);
    setLimit(DEFAULT_LIMIT);
    setFilters({});
    setLocalEdits({});
    setRemovedCodes([]);
    setShouldShowControls(false);
  }, [isOpen]);

  return {
    selectedRows,
    standardChargeMultiplier,
    allowableMultiplier,
    selfPayMultiplier,
    selectedSpecialty,
    page,
    limit,
    filters,
    localEdits,
    shouldShowControls,
    tabsData,
    paginatedAndFilteredData,
    totalItems,
    showControls,
    isLoading,
    isFetching,
    isError,
    setSelectedSpecialty: handleSpecialtyChange,
    setPage,
    setLimit: handleLimitChange,
    setLocalEdits,
    changeFilters,
    handleStandardChargeChange,
    handleAllowableChange,
    handleSelfPayChange,
    handleAddServices,
    handleCreateNewService,
    onSelectionChanged,
    removeCode,
    retry,
    refetchCptCodes
  };
};
